import { get } from 'svelte/store';

// import { format } from 'svelte-i18n';
import CacheStatisticRequest from './statistics/request/chart_cache_statistics_helpers';

import { profile_data, user_data, user_settings } from 'src/stores/account/user';
import { firmwares_gen_2 } from 'src/stores/device-runtime/process';
import { app, os } from 'src/stores/session/native-bridge';

import { api, clearToken, standardLogin } from '../services/http';
import { defaultAppBackgroundSrc } from './constants';

import {
	logout_all_android,
	logout_mobile_android,
} from '../services/native_container_requests_android';
import { logout_all_ios, logout_mobile_ios } from '../services/native_container_requests_ios';

import { load_image_from } from '../helpers/interface_helpers';
import { getBackgroundColorImage } from './background_color_helpers';
import { profiles } from 'src/stores/features/profiles';
import { setUpI18n } from 'src/services/i18n';
import { socket_disconnect } from 'src/services/socket';
import { handleUserProfile, removeProfile } from './login_helpers';

import type { MenuListDataType } from 'src/components/Profiles/DropdownItem.types';
import type { AvailableSubUnits, AvailableUnitsSelection } from 'src/types/responses/usersAuthResponse';
import { isMultiUnitResult } from 'src/services/auth_v2';
import { toast } from '@Shelly/shelly-design-system';
import { replace } from 'svelte-spa-router';
import { stopAndClearNotifications } from './notifications/device_notifications_helper';
import { resetDrawer } from './drawer_helpers';
import { handleLegacyLoginErrors } from 'src/services/php-api/legacy/v1_auth';
import {
	handleAuthErrorMessage,
	shouldForceLogout,
	type AuthErroMessage,
} from 'src/services/auth_errors';
import { compare } from 'compare-versions';
import { clearTokenWatch } from 'src/services/auth_token';
import type { UserData } from 'src/types/UserData';
import { clearCameraStores, clearCameraCredentials } from 'src/components/Camera/helpers/camera-helpers';
import { cameraServicesUnload } from 'src/components/Camera/helpers/camera-cloud';
import { aiSceneChatStore } from 'src/stores/features/ai-scene-chat';
import { resetAllStores } from 'src/stores/reset';
import { clearHomes, clearPrimedUnitsInfo } from 'src/stores/account/home';
import { clearUnitTokenCache, invalidateAccountSessions } from 'src/services/unit_token_cache';
import { clearEnergyHeatmapCache } from 'src/components/EnergyMonitoring/helpers/energy_heatmap_cache';
import { resetEnergyMonitoringForAccountChange } from 'src/components/EnergyMonitoring/helpers/store';
import { clearPendingUnitTransfers } from 'src/stores/account/pending_unit_transfers';
import { resetSubUnitsCache } from 'src/components/Scenes/helpers/notification_recipients_helpers';
import { normalizeEmail } from 'src/helpers/email_helpers';

export type HandleLogoutOptions = {
	/**
	 * Skip auto-switching to another saved profile.
	 * Always clear local/native session state and redirect to `/login`.
	 * Used by force-logout paths (session invalid, email change, account removed).
	 */
	forceToLogin?: boolean;
};

/**
 * Clears user-related data on login
 */
export const clearDataOnLogin = () => {
	//@ToDo: Review this function; Moved here from standardLogin
	CacheStatisticRequest.clear();
	clearEnergyHeatmapCache();
	firmwares_gen_2.reset();
	clearCameraStores();
	resetEnergyMonitoringForAccountChange();
	// Profile-switching path: the new account's sub-units are scoped to its
	// own home, so any cached entries from the previous account must be
	// dropped before the wizard or side-panel queries them.
	resetSubUnitsCache();
	clearPendingUnitTransfers();

	resetDrawer('login');
	aiSceneChatStore.clearAllData();
	profile_data.set({});
};

export const clearAllData = () => {
	clearTokenWatch();
	clearToken();
	resetAllStores();
	sessionStorage.clear();
	CacheStatisticRequest.clear();
	clearEnergyHeatmapCache();
	aiSceneChatStore.clearAllData();

	clearCameraStores();
	clearHomes();
	clearPrimedUnitsInfo();
	clearPendingUnitTransfers();
	resetSubUnitsCache();
	clearUnitTokenCache();
};

export async function handleLogout(
	selectedProfile?: void | MenuListDataType,
	logoutAllProfiles?: boolean,
	options?: HandleLogoutOptions,
) {
	const email = get(user_data).email;
	const normalizedEmail = normalizeEmail(email);
	const token = user_data.getToken();

	// Live streams must be torn down while their socket is still up.
	cameraServicesUnload();

	//Disconnect socket
	socket_disconnect();

	// Server may already have invalidated the session (e.g. email change).
	// Local/native cleanup must still run so clients are not left half-authed.
	try {
		await api.post(
			'/auth/logout',
			{},
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		);
	} catch (error) {
		console.warn('[AUTH] Server logout failed; continuing local cleanup');
	}

	clearToken();

	// Drop the logged-out account's cached unit tokens so its homes can't be
	// re-served from cache when switching to another profile (which skips
	// `clearAllData()`).
	invalidateAccountSessions(email);

	let backwardsCompatibilityLogoutAll = false;
	if (get(app)) {
		const regex = /Shelly.*?[-\/](\d+\.\d+\.\d+)/;
		const match = navigator.userAgent.match(regex);
		const nativeVersion = match ? match[1] : '0.0.1';
		if (get(os) === 'IOS') {
			logoutAllProfiles
				? compare(nativeVersion, '1.7.0', '>')
					? logout_all_ios() // iOS - it will be available with 1.7.1 and above
					: (backwardsCompatibilityLogoutAll = true)
				: logout_mobile_ios(normalizedEmail);
		} else {
			logoutAllProfiles
				? compare(nativeVersion, '1.30.4', '>') && +nativeVersion.split('.')[1] > 30 // second number represents the real Android version, so we need also this check
					? await logout_all_android() // Android - it will be available with 1.30.5 and above
					: (backwardsCompatibilityLogoutAll = true)
				: await logout_mobile_android(normalizedEmail);
		}
	}

	// Stop and clear notifications on every logout
	stopAndClearNotifications();

	if (logoutAllProfiles) {
		for (const profile of get(profiles)) {
			if (backwardsCompatibilityLogoutAll) {
				const profileEmail = normalizeEmail(profile.email);
				if (get(os) === 'IOS') {
					logout_mobile_ios(profileEmail);
				} else {
					await logout_mobile_android(profileEmail);
				}
			}
			removeProfile(profile.email);
		}

		clearCameraCredentials();
		clearAllData();
		return;
	}

	clearCameraCredentials(email);

	if (options?.forceToLogin) {
		const activeProfile = get(profiles).find((p) => normalizeEmail(p.email) === normalizedEmail);
		if (activeProfile) {
			profiles.remove(activeProfile.email);
		}
		clearAllData();
		replace('/login');
		return;
	}

	const activeProfile = get(profiles).find((p) => normalizeEmail(p.email) === normalizedEmail);
	const nextProfile: void | MenuListDataType =
		typeof activeProfile !== 'undefined' ? removeProfile(activeProfile.email) : undefined;

	if (typeof nextProfile !== 'undefined' && typeof selectedProfile === 'undefined') {
		return await switchingProfile(nextProfile);
	} else if (typeof selectedProfile !== 'undefined') {
		return await switchingProfile(selectedProfile);
	} else {
		clearAllData();
		replace('/login');
	}
}

/**
 * Handles the logout process for a specific user profile.
 *
 * @param userData User data object containing token and email
 * @returns {Promise<void>} - A promise that resolves when the logout is complete
 */
export const handleSingleProfileLogout = async (userData: UserData): Promise<void> => {
	// Server may already have invalidated the session. Local/native cleanup
	// must still run so a replacement login can proceed (FE-2492).
	try {
		await api.post(
			'/auth/logout',
			{},
			{
				headers: {
					Authorization: `Bearer ${userData?.token}`,
				},
			},
		);
	} catch {
		console.warn('[AUTH] Server logout failed; continuing local cleanup');
	}

	const normalizedEmail = normalizeEmail(userData?.email);
	const match = get(profiles).find((profile) => normalizeEmail(profile.email) === normalizedEmail);
	const email = match?.email ?? normalizedEmail;
	if (match?.email) {
		removeProfile(match.email);
	}
	clearCameraCredentials(email);

	if (get(app) && email) {
		if (get(os) === 'IOS') {
			logout_mobile_ios(email);
		} else {
			await logout_mobile_android(email);
		}
	}
};

export type PendingUnitSelection = {
	pendingUnitSelection: true;
	profileId: MenuListDataType['id'];
	profileSub: MenuListDataType['sub'];
	availableSubUnits: AvailableSubUnits;
} & AvailableUnitsSelection;

/**
 * Function for switching profile to next available.
 *  Handles switching profile after logout.
 * * @param {MenuListDataType} profile - The profile to switch to.
 * * @returns {Promise<PendingUnitSelection | void>} - Multi-unit info if the user needs to pick a home, void otherwise.
 */
const switchingProfile = async (profile: MenuListDataType): Promise<PendingUnitSelection | void> => {
	let res = await standardLogin(profile.email, profile.pass);

	if (res?.isok && isMultiUnitResult(res)) {
		const ownedHomes = Object.entries(res.availableUnits ?? {});
		const sharedHomes = Object.entries(res.availableSubUnits ?? {});
		const totalAvailableHomes = ownedHomes.length + sharedHomes.length;
		const knownAvailableUnits = res.availableUnits;
		const knownAvailableSubUnits = res.availableSubUnits ?? {};
		const hasTokens = res.accessToken && res.refreshToken;

		if (totalAvailableHomes > 1) {
			return {
				pendingUnitSelection: true,
				profileId: profile.id,
				profileSub: profile.sub,
				availableUnits: knownAvailableUnits,
				availableSubUnits: knownAvailableSubUnits,
				// Discovery token for the target account so the picker can render
				// from its own `get_units_info` (account-scoped, no global pollution).
				discoveryToken: res.data?.token,
				discoveryApiUrl: res.data?.user_api_url,
			};
		}

		if (!hasTokens) {
			let selectedHomeId: string | undefined;
			let sharedHomeId: string | undefined;

			if (ownedHomes.length === 1) {
				selectedHomeId = ownedHomes[0][0];
			} else {
				const [homeId, homeValue] = sharedHomes[0];
				selectedHomeId = homeId;
				sharedHomeId =
					typeof homeValue === 'object' ? String((homeValue as any).sub) : undefined;
			}

			res = await standardLogin(profile.email, profile.pass, selectedHomeId, sharedHomeId);
		}
	}

	if (res?.isok) {
		clearDataOnLogin();
		setUpI18n(res.data.lang);
		await handleUserProfile(profile.id);
	} else {
		const alertProps = handleLoginErrors(res);
		toast.open({
			type: 'danger',
			title: alertProps.heading,
			text: alertProps.message,
		});

		// Handle invalid session error and remove profile
		// E.g. wrong credentials, too many attempts, etc.
		if (shouldForceLogout(res as Parameters<typeof shouldForceLogout>[0])) {
			profiles.remove(profile.email);
			clearCameraCredentials(profile.email);
			if (get(profiles).length <= 0) {
				// Reset profiles and redirect to login if no profiles remain
				console.warn('[AUTH] No profiles left after logout, resetting profiles store');
				profiles.reset();
				clearAllData();
				replace('/login');
			} else {
				// Switching to the next available profile
				await switchingProfile(get(profiles)[0]);
			}
		}
	}
};

export function getAppBackgroundSrc(profileData: any, useLoadImg = false, userApiUrl?: string) {
	// backgroundColor tokens (solid colors and gradients alike) resolve to a pre-rendered
	// image (see background_color_helpers.ts) instead of a computed CSS background/gradient.
	const backgroundColorImage = getBackgroundColorImage(profileData?.layout?.backgroundColor);
	if (backgroundColorImage) {
		return useLoadImg
			? load_image_from(backgroundColorImage.full, userApiUrl)
			: backgroundColorImage.full;
	}

	// Any backgroundColor token without a pre-rendered image (e.g. legacy/unmapped values)
	// falls back to the design system's own CSS class rendering - no image path.
	if (profileData?.layout?.backgroundColor) {
		return '';
	}

	const appBackgroud = profileData?.layout?.appBackgroud || defaultAppBackgroundSrc;
	return useLoadImg ? load_image_from(appBackgroud, userApiUrl) : appBackgroud;
}

/** Whether the profile's current backgroundColor has a pre-rendered image (see background_color_helpers.ts). */
export function hasBackgroundColorImage(profileData: any): boolean {
	return !!getBackgroundColorImage(profileData?.layout?.backgroundColor);
}

export function applicationHostUrl(url: string = undefined, route: string = undefined) {
	// this is used in cases like forgotten password to generate some source urls as reset password button's address from email address
	// if (get(app)) {
	// 	url = 'https://control.shelly.cloud'; //hardcoded until we provide a proper way to get src from containers
	// }
	return `${url.includes('http') ? url : 'https://' + url}` + `${route ? route : ''}`;
}

/**
 * Handle Login Errors - PHP API Legacy login
 * @deprecated : Deprecate after full migration to iotu v2 login
 * - Use `handleLegacyLoginErrors` for the PHP-API login services.
 * - Use auth_error `handleAuthError` for generic auth errors.
 *
 * This function processes the errors received during the login attempt and returns an object with alert properties.
 * It uses the legacy login error handler for compatibility with existing code.
 * @param response Api response object containing `errors` or `error` object
 * @param email
 * @returns  {AuthErroMessage}	Alert props
 */
export function handleLoginErrors(response: any, email?: string): AuthErroMessage {
	// Legace PHP API login error handling
	if (response?.errors) {
		return handleLegacyLoginErrors(response?.errors, email);
	}
	// Handle auth errors
	return handleAuthErrorMessage(response, email);
}

export function isSupportAccessExpired(supportAccessExpiryTimestamp: number): boolean {
	return (
		Number.isFinite(supportAccessExpiryTimestamp) &&
		supportAccessExpiryTimestamp < new Date().getTime()
	);
}
